/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type CreateTemplateInput,
  type TemplateDetail,
  type TemplateSummary,
  templatesApi,
} from '../api/templates.js';

const TEMPLATES_KEY = ['itp-templates'] as const;

export function useTemplates(): UseQueryResult<readonly TemplateSummary[]> {
  return useQuery({ queryKey: TEMPLATES_KEY, queryFn: () => templatesApi.list() });
}

export function useCreateTemplate(): UseMutationResult<TemplateDetail, Error, CreateTemplateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTemplateInput) => templatesApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

export function useDeleteTemplate(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => templatesApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}
